import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const DesignsPage = () => {
    const [selectedDesign, setSelectedDesign] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);

    const designs = [
        {
            id: 1,
            title: "Mechanized Heart",
            description: "Exploring the boundaries between human and machine.",
            imageUrl: "/gallery/chappie-poster-1.png",
            tags: ["Cybernetic", "Transformation", "Identity"]
        },
        {
            id: 2,
            title: "Aphex Twin",
            description: "Tribute to Selected Ambient Works 85-92.",
            imageUrl: "/gallery/aphex-twin-poster-1.png",
            tags: ["Music"]
        },
        {
            id: 3,
            title: "Space Blues",
            description: "",
            imageUrl: "/gallery/space-blues-poster-1.png",
            tags: ["Space", "Perception", "Boundaries"]
        },
        {
            id: 4,
            title: "Iteration Body Labs",
            description: "Mapping consciousness beyond biological limitations.",
            imageUrl: "/gallery/iteration-body-labs-poster-1.png",
            tags: ["Body", "Technology", "Interface"]
        },
        {
            id: 5,
            title: "Sentient Studios",
            description: "Brutalist temple.",
            imageUrl: "/gallery/sentient-labs-poster-1.png",
            tags: ["Architecture", "Eternity", "Consciousness"]
        },
        {
            id: 6,
            title: "Exit Death Records",
            description: "",
            imageUrl: "/gallery/exit-death-records-v2.png",
            tags: ["Music", "Art", "Identity"]
        },
        {
            id: 7,
            title: "Cryptic Beauty",
            description: "Chaos",
            imageUrl: "/gallery/chaos-1-v2.png",
            tags: [],
        },
        {
            id: 8,
            title: "Anthropic",
            description: "",
            imageUrl: "/gallery/anthropic-poster-1.png",
            tags: []
        }
    ];

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setMousePosition({
                    x: ((e.clientX - rect.left) / rect.width) * 100,
                    y: ((e.clientY - rect.top) / rect.height) * 100
                });
            }
        };

        const currentContainer = containerRef.current;
        if (currentContainer) {
            currentContainer.addEventListener('mousemove', handleMouseMove);
            return () => {
                currentContainer.removeEventListener('mousemove', handleMouseMove);
            };
        }
    }, []);

    const DesignCard = ({ design, isSelected, onSelect }) => {
        return (
            <div
                className={`
                    relative group cursor-pointer 
                    transform transition-all duration-500
                    ${isSelected
                    ? 'scale-110 z-50 shadow-2xl'
                    : 'hover:scale-105'}
                `}
                onClick={() => onSelect(isSelected ? null : design)}
            >
                <div
                    className="absolute inset-0 bg-gradient-to-br from-purple-900 to-blue-900 opacity-20 blur-lg group-hover:opacity-30 transition-all duration-500"
                ></div>
                <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
                    <div
                        className="w-full aspect-[1/1.414] bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                        style={{
                            backgroundImage: `url(${design.imageUrl})`,
                            transform: isSelected ? 'scale(1.2)' : 'scale(1)'
                        }}
                    />
                    <div className="p-4">
                        <h3 className="text-xl mb-2">{design.title}</h3>
                        {isSelected && (
                            <div className="space-y-2 animate-fade-in">
                                <p className="text-sm text-gray-300">{design.description}</p>
                                <div className="flex flex-wrap gap-2">
                                    {design.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="px-2 py-1 bg-white/10 text-xs rounded-full"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            ref={containerRef}
            className="min-h-screen w-full bg-black text-white font-mono relative overflow-hidden"
            style={{
                backgroundImage: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(30,30,30,0.9), rgba(0,0,0,1))`,
                backgroundSize: 'cover',
                transition: 'background-position 0.5s ease'
            }}
        >
            <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-purple-900 via-transparent to-blue-900"></div>

            <div className="relative z-10 max-w-7xl mx-auto px-8 py-16">
                {/* Header */}
                <div className="mb-24 flex justify-between items-start">
                    <div>
                        <Link
                            to="/"
                            className="inline-flex items-center space-x-2 text-sm hover:opacity-70 transition-opacity mb-4"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Exit Death</span>
                        </Link>
                        <h1 className="text-4xl tracking-tight mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                            Designs
                        </h1>
                        <p className="text-xl max-w-xl opacity-70">
                            Some designs by the studio. Click to view more details.
                        </p>
                    </div>
                </div>

                {/* Designs Grid */}
                <div className="grid grid-cols-2 gap-8">
                    {designs.map((design) => (
                        <DesignCard
                            key={design.id}
                            design={design}
                            isSelected={selectedDesign?.id === design.id}
                            onSelect={setSelectedDesign}
                        />
                    ))}
                </div>

                {/* Expanded View */}
                {selectedDesign && (
                    <div
                        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-12"
                        onClick={() => setSelectedDesign(null)}
                    >
                        <div
                            className="max-w-4xl w-full grid grid-cols-2 gap-8"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div
                                className="aspect-[1/1.414] bg-cover bg-center rounded-xl"
                                style={{ backgroundImage: `url(${selectedDesign.imageUrl})` }}
                            />
                            <div className="flex flex-col justify-center">
                                <h2 className="text-3xl mb-4">{selectedDesign.title}</h2>
                                <p className="text-gray-300 mb-6">{selectedDesign.description}</p>
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {selectedDesign.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 bg-white/10 rounded-full"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <button
                                    className="self-start px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full transition-all"
                                    onClick={() => setSelectedDesign(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DesignsPage;
